import { format } from "date-fns";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  createPlanService,
  getPatientDiagnosesService,
  getPlanByIdService,
  getStaffService,
  getUserByIdService,
  updatePlanService,
} from "../api/services";
import { DatePicker } from "../components/DatePicker";
import { useToast } from "../components/Toast";
import { useRequestError } from "../hooks/useRequestError";
import type { UserModel } from "../types/models";
import type { DiagnosisSummaryParams } from "../types/params";
import { getDateObject } from "../utils/date";
import {
  buildDiagnosisMutationsPayload,
  mapBackendDiagnosesToSummary,
  mergeDiagnosesWithMutations,
  type DiagnosisMutationsState,
} from "../utils/diagnosisFlow";

type PlanFormState = {
  doctor_id: string;
  start_date: string;
  end_date: string;
  steps_good: [number, number];
  sleep_good: [number, number];
  steps_warning: [number, number];
  sleep_warning: [number, number];
  blood_systolic_pressure_good: [number, number];
  blood_systolic_pressure_warning: [number, number];
  blood_diastolic_pressure_good: [number, number];
  blood_diastolic_pressure_warning: [number, number];
  blood_glucose_good: [number, number];
  blood_glucose_warning: [number, number];
  heart_rate_good: [number, number];
  heart_rate_warning: [number, number];
  prescription: Array<{ name: string; dosage: string }>;
};

const defaults: PlanFormState = {
  doctor_id: "",
  start_date: "",
  end_date: "",
  steps_good: [8000, 15000],
  sleep_good: [6, 9],
  steps_warning: [1500, 25000],
  sleep_warning: [4, 12],
  blood_systolic_pressure_good: [80, 120],
  blood_systolic_pressure_warning: [50, 150],
  blood_diastolic_pressure_good: [80, 120],
  blood_diastolic_pressure_warning: [50, 150],
  blood_glucose_good: [80, 120],
  blood_glucose_warning: [50, 150],
  heart_rate_good: [80, 120],
  heart_rate_warning: [50, 150],
  prescription: [{ name: "", dosage: "" }],
};

const toInputDate = (value?: string) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 10);
  return d.toISOString().slice(0, 10);
};

export const PlanSetupPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { handleRequestError } = useRequestError();
  const userId = params.get("user_id") || "";
  const planId = params.get("plan_id") || "";
  const isEditing = Boolean(planId);

  const [form, setForm] = useState<PlanFormState>(defaults);
  const [patient, setPatient] = useState<UserModel | null>(null);
  const [doctors, setDoctors] = useState<UserModel[]>([]);
  const [selectedDiagnoses, setSelectedDiagnoses] = useState<
    DiagnosisSummaryParams[]
  >([]);
  const [diagnosisMutations, setDiagnosisMutations] =
    useState<DiagnosisMutationsState>({
      add: [],
      remove_ids: [],
      removed_existing_codes: [],
    });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!userId) return;
      setLoading(true);
      try {
        const [staff, patientRes, diagnosesRes] = await Promise.all([
          getStaffService({
            limit: 50,
            offset: 0,
            only_doctors: true,
            patient_id: userId,
          }),
          getUserByIdService(userId),
          getPatientDiagnosesService(userId),
        ]);
        setDoctors(staff.result_list || []);
        setPatient(patientRes);

        let routeSelected: DiagnosisSummaryParams[] = [];
        let routeMutations: DiagnosisMutationsState = {
          add: [],
          remove_ids: [],
          removed_existing_codes: [],
        };
        try {
          const rawSelected = sessionStorage.getItem(
            "reeva.plan.selectedDiagnoses"
          );
          const rawMutations = sessionStorage.getItem(
            "reeva.plan.diagnosisMutations"
          );
          if (rawSelected) routeSelected = JSON.parse(rawSelected);
          if (rawMutations) routeMutations = JSON.parse(rawMutations);
        } catch {
          // ignore
        }

        const backendDiagnoses = mapBackendDiagnosesToSummary(
          diagnosesRes || []
        );
        setSelectedDiagnoses(
          mergeDiagnosesWithMutations({
            backendDiagnoses,
            routeSelectedDiagnoses: routeSelected,
            mutations: routeMutations,
          })
        );
        setDiagnosisMutations(routeMutations);

        // Require an explicit doctor pick on create (same as mobile).
        let nextForm: PlanFormState = {
          ...defaults,
          doctor_id: "",
        };

        if (planId) {
          const plan = await getPlanByIdService(planId);
          nextForm = {
            ...nextForm,
            doctor_id: plan.doctor_id || "",
            start_date: toInputDate(plan.start_date),
            end_date: toInputDate(plan.end_date),
            steps_good: plan.data.lifestyle.steps_count.good,
            steps_warning: plan.data.lifestyle.steps_count.warning,
            sleep_good: plan.data.lifestyle.sleep_hours.good,
            sleep_warning: plan.data.lifestyle.sleep_hours.warning,
            blood_systolic_pressure_good:
              plan.data.vitals.blood_systolic_pressure.good,
            blood_systolic_pressure_warning:
              plan.data.vitals.blood_systolic_pressure.warning,
            blood_diastolic_pressure_good:
              plan.data.vitals.blood_diastolic_pressure.good,
            blood_diastolic_pressure_warning:
              plan.data.vitals.blood_diastolic_pressure.warning,
            blood_glucose_good: plan.data.vitals.blood_glucose.good,
            blood_glucose_warning: plan.data.vitals.blood_glucose.warning,
            heart_rate_good: plan.data.vitals.heart_rate.good,
            heart_rate_warning: plan.data.vitals.heart_rate.warning,
            prescription:
              plan.data.prescription?.length > 0
                ? plan.data.prescription
                : [{ name: "", dosage: "" }],
          };
        }
        setForm(nextForm);
      } catch (error) {
        handleRequestError(error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId, planId, handleRequestError]);

  const setRange = (
    key:
      | "steps_good"
      | "steps_warning"
      | "sleep_good"
      | "sleep_warning"
      | "blood_systolic_pressure_good"
      | "blood_systolic_pressure_warning"
      | "blood_diastolic_pressure_good"
      | "blood_diastolic_pressure_warning"
      | "blood_glucose_good"
      | "blood_glucose_warning"
      | "heart_rate_good"
      | "heart_rate_warning",
    index: 0 | 1,
    value: string
  ) => {
    setForm((prev) => {
      const next = [...prev[key]] as [number, number];
      next[index] = Number(value);
      return { ...prev, [key]: next };
    });
  };

  const todayDate = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  const startDateMin = todayDate;
  const endDateMin =
    form.start_date && form.start_date > todayDate
      ? form.start_date
      : todayDate;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.doctor_id.trim()) {
      showToast({
        type: "error",
        title: "Doctor required",
        message: "Please select a doctor for this care plan.",
      });
      return;
    }
    if (!form.start_date || !form.end_date) {
      showToast({
        type: "error",
        title: "Missing fields",
        message: "Start date and end date are required.",
      });
      return;
    }
    if (!isEditing && form.start_date < todayDate) {
      showToast({
        type: "error",
        title: "Invalid start date",
        message: "Start date cannot be in the past.",
      });
      return;
    }
    if (!isEditing && form.end_date < todayDate) {
      showToast({
        type: "error",
        title: "Invalid end date",
        message: "End date cannot be in the past.",
      });
      return;
    }
    if (form.end_date < form.start_date) {
      showToast({
        type: "error",
        title: "Invalid end date",
        message: "End date must be on or after the start date.",
      });
      return;
    }
    setSaving(true);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 30000);
    try {
      const payloadData = {
        lifestyle: {
          steps_count: {
            good: form.steps_good,
            warning: form.steps_warning,
          },
          sleep_hours: {
            good: form.sleep_good,
            warning: form.sleep_warning,
          },
        },
        vitals: {
          blood_systolic_pressure: {
            good: form.blood_systolic_pressure_good,
            warning: form.blood_systolic_pressure_warning,
          },
          blood_diastolic_pressure: {
            good: form.blood_diastolic_pressure_good,
            warning: form.blood_diastolic_pressure_warning,
          },
          blood_glucose: {
            good: form.blood_glucose_good,
            warning: form.blood_glucose_warning,
          },
          heart_rate: {
            good: form.heart_rate_good,
            warning: form.heart_rate_warning,
          },
        },
        prescription: form.prescription.filter(
          (item) => item.name.trim() || item.dosage.trim()
        ),
      };
      const diagnosis_mutations =
        buildDiagnosisMutationsPayload(diagnosisMutations);

      if (isEditing && planId) {
        await updatePlanService(
          planId,
          {
            start_date: getDateObject(form.start_date),
            end_date: getDateObject(form.end_date),
            data: payloadData,
            diagnosis_mutations,
          },
          controller.signal
        );
      } else {
        await createPlanService(
          {
            doctor_id: form.doctor_id,
            patient_id: userId,
            start_date: getDateObject(form.start_date),
            end_date: getDateObject(form.end_date),
            data: payloadData,
            diagnosis_mutations,
          },
          controller.signal
        );
      }
      sessionStorage.removeItem("reeva.plan.selectedDiagnoses");
      sessionStorage.removeItem("reeva.plan.diagnosisMutations");
      showToast({
        type: "success",
        title: "Saved",
        message: isEditing ? "Plan updated." : "Plan created.",
      });
      navigate("/");
    } catch (error) {
      handleRequestError(error);
    } finally {
      window.clearTimeout(timeout);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="spinner" />
      </div>
    );
  }

  const diagnosisQuery = new URLSearchParams({ user_id: userId });
  if (planId) diagnosisQuery.set("plan_id", planId);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>{isEditing ? "Edit plan" : "Create plan"}</h1>
          {patient ? (
            <p className="muted">
              {patient.first_name} {patient.last_name}
            </p>
          ) : null}
        </div>
        <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
          Back
        </button>
      </header>

      <form className="stack gap-lg" onSubmit={onSubmit}>
        <section className="panel form-grid">
          <label className="field">
            <span>Doctor *</span>
            <select
              value={form.doctor_id}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, doctor_id: e.target.value }))
              }
              required
              disabled={isEditing}
            >
              <option value="" disabled>
                Select doctor…
              </option>
              {doctors.map((doctor) => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.first_name} {doctor.last_name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Start date *</span>
            <DatePicker
              value={form.start_date}
              onChange={(next) =>
                setForm((prev) => ({
                  ...prev,
                  start_date: next,
                  end_date:
                    prev.end_date && next && prev.end_date < next
                      ? ""
                      : prev.end_date,
                }))
              }
              min={startDateMin}
              placeholder="Select start date"
              required
            />
          </label>
          <label className="field">
            <span>End date *</span>
            <DatePicker
              value={form.end_date}
              onChange={(next) =>
                setForm((prev) => ({ ...prev, end_date: next }))
              }
              min={endDateMin}
              placeholder="Select end date"
              required
            />
          </label>
        </section>

        <section className="panel stack gap-md">
          <div className="row-between">
            <h3>Diagnoses</h3>
            <Link
              className="btn btn-secondary"
              to={`/diagnoses?${diagnosisQuery.toString()}`}
            >
              Manage diagnoses
            </Link>
          </div>
          {selectedDiagnoses.length === 0 ? (
            <p className="muted">No diagnoses selected.</p>
          ) : (
            <ul className="diagnosis-selected-list">
              {selectedDiagnoses.map((item) => (
                <li key={item.icd_code} className="diagnosis-selected-item">
                  <div className="diagnosis-selected-text">
                    <strong className="diagnosis-selected-title">
                      {item.title}
                    </strong>
                    <span className="diagnosis-selected-code">
                      {item.icd_code}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="panel">
          <h3>Lifestyle ranges</h3>
          <div className="form-grid">
            {(
              [
                ["steps_good", "Steps good"],
                ["steps_warning", "Steps warning"],
                ["sleep_good", "Sleep good"],
                ["sleep_warning", "Sleep warning"],
              ] as const
            ).map(([key, label]) => (
              <div key={key} className="field">
                <span>{label}</span>
                <div className="range-inputs">
                  <input
                    type="number"
                    value={form[key][0]}
                    onChange={(e) => setRange(key, 0, e.target.value)}
                  />
                  <input
                    type="number"
                    value={form[key][1]}
                    onChange={(e) => setRange(key, 1, e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="panel">
          <h3>Vitals ranges</h3>
          <div className="form-grid">
            {(
              [
                ["blood_systolic_pressure_good", "Systolic good"],
                ["blood_systolic_pressure_warning", "Systolic warning"],
                ["blood_diastolic_pressure_good", "Diastolic good"],
                ["blood_diastolic_pressure_warning", "Diastolic warning"],
                ["blood_glucose_good", "Glucose good"],
                ["blood_glucose_warning", "Glucose warning"],
                ["heart_rate_good", "Heart rate good"],
                ["heart_rate_warning", "Heart rate warning"],
              ] as const
            ).map(([key, label]) => (
              <div key={key} className="field">
                <span>{label}</span>
                <div className="range-inputs">
                  <input
                    type="number"
                    value={form[key][0]}
                    onChange={(e) => setRange(key, 0, e.target.value)}
                  />
                  <input
                    type="number"
                    value={form[key][1]}
                    onChange={(e) => setRange(key, 1, e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="panel stack gap-md">
          <div className="row-between">
            <h3>Prescription</h3>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  prescription: [...prev.prescription, { name: "", dosage: "" }],
                }))
              }
            >
              Add medication
            </button>
          </div>
          {form.prescription.map((item, index) => (
            <div key={index} className="prescription-item">
              <div className="row-between">
                <h4 className="prescription-item-title">
                  Medication {index + 1}
                </h4>
                {form.prescription.length > 1 ? (
                  <button
                    type="button"
                    className="text-btn danger"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        prescription: prev.prescription.filter(
                          (_, i) => i !== index
                        ),
                      }))
                    }
                  >
                    Remove
                  </button>
                ) : null}
              </div>
              <div className="form-grid">
                <label className="field">
                  <span>Name</span>
                  <input
                    value={item.name}
                    placeholder="Medication name"
                    onChange={(e) => {
                      const next = [...form.prescription];
                      next[index] = { ...item, name: e.target.value };
                      setForm((prev) => ({ ...prev, prescription: next }));
                    }}
                  />
                </label>
                <label className="field">
                  <span>Dosage</span>
                  <input
                    value={item.dosage}
                    placeholder="e.g. 250mg per day"
                    onChange={(e) => {
                      const next = [...form.prescription];
                      next[index] = { ...item, dosage: e.target.value };
                      setForm((prev) => ({ ...prev, prescription: next }));
                    }}
                  />
                </label>
              </div>
            </div>
          ))}
        </section>

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? "Saving…" : isEditing ? "Update plan" : "Create plan"}
        </button>
      </form>
    </div>
  );
};
